import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { InspectionTemplate, TemplateSection } from 'inspection-types';

function findScrollContainer(el: Element): Element | null {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (/auto|scroll/.test(overflow + overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null; // null = browser viewport
}

type Params = {
  template: InspectionTemplate | undefined;
  sortedSections: TemplateSection[];
  setActiveSectionId: (sectionId: string) => void;
  isScrollingRef: MutableRefObject<boolean>;
};

export function useInspectionTemplateCustomizeSectionObserver({
  template,
  sortedSections,
  setActiveSectionId,
  isScrollingRef,
}: Params): void {
  useEffect(() => {
    if (!template || sortedSections.length === 0) {
      return undefined;
    }

    const elements = sortedSections
      .map(s => document.getElementById(`section-${s.id}`))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) {
      return undefined;
    }

    // Resolve the actual scroll container at observer-setup time so rootMargin
    // is applied against the element that is really scrolling. If no overflow
    // ancestor is found we fall back to null (browser viewport).
    const scrollContainer = findScrollContainer(elements[0]);

    // Tracks which section IDs are currently inside the active zone.
    // Maintained across IO callbacks so we always have the full visible set,
    // not just the entries that changed in the current batch.
    const visibleIds = new Set<string>();

    const observer = new IntersectionObserver(
      entries => {
        if (isScrollingRef.current) {
          return;
        }

        // Update visibility for each entry that changed.
        entries.forEach(entry => {
          const id = entry.target.id.replace('section-', '');
          if (entry.isIntersecting) {
            visibleIds.add(id);
          } else {
            visibleIds.delete(id);
          }
        });

        // Pick the first visible section in document order (topmost on screen).
        const active = sortedSections.find(s => visibleIds.has(s.id));
        if (active) {
          setActiveSectionId(active.id);
        }
      },
      {
        root: scrollContainer,
        rootMargin: '-68px 0px -55% 0px', // offset for fixed $header-height (4.25rem = 68px)
        // threshold: 0 fires only when a section enters or exits the active zone,
        // not on every incremental ratio change — eliminates the highlight flicker.
        threshold: 0,
      }
    );

    elements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
      visibleIds.clear();
    };
  // isScrollingRef intentionally omitted: useRef returns a stable object for the
  // component's lifetime, and the callback reads .current at call time — it is
  // not a setup-time value that needs to trigger observer recreation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, sortedSections, setActiveSectionId]);
}
